/**
 * Tracking Helper - GTM & Meta Pixel Events
 * Centralized tracking for all app.sellagent.ai events
 */

import { GTM_TRACK_MODE } from '../store/config';

// ============ CORE HELPER FUNCTIONS ============

/**
 * Get user data and tracking from localStorage
 * @returns {Object} { user, tracking }
 */
const getUserData = () => {
  if (typeof window === 'undefined') return { user: null, tracking: {} };

  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const tracking = user?.tracking || {};

  return { user, tracking };
};

/**
 * Check if event should be tracked based on GTM_TRACK_MODE
 * @param {Object} tracking - Tracking object
 * @returns {boolean}
 */
const shouldTrack = (tracking) => {
  return GTM_TRACK_MODE === 0 || (GTM_TRACK_MODE === 1 && tracking?.trackingId && tracking.trackingId !== 'none');
};

/**
 * Send event to Google Tag Manager (GTM)
 * @param {string} eventName - GTM event name
 * @param {Object} eventData - Event data to send
 * @param {Object} tracking - Tracking object
 */
export const sendGTMEvent = (eventName, eventData = {}, tracking = {}) => {
  if (typeof window === 'undefined' || !window.dataLayer) return;

  if (shouldTrack(tracking)) {
    window.dataLayer.push({
      event: eventName,
      ...eventData,
      ...(tracking || {})
    });
    console.log(`🟢 [GTM] ${eventName}`, tracking);
  }
};

/**
 * Send event to Meta Pixel (Facebook Pixel)
 * @param {string} eventName - Meta standard event name
 * @param {Object} eventData - Event data to send
 * @param {Object} tracking - Tracking object
 */
export const sendMetaEvent = (eventName, eventData = {}, tracking = {}) => {
  if (typeof window === 'undefined' || !window.fbq) return;

  if (shouldTrack(tracking)) {
    window.fbq('track', eventName, {
      ...eventData,
      ...(tracking || {})
    });
    console.log(`🟣 [META] ${eventName} - ${eventData.content_name || ''}`, tracking);
  }
};

// ============ APP SPECIFIC EVENTS ============

/**
 * Track user signup complete (email verification)
 * @param {Object} userData - User object from API response
 */
export const trackUserSignup = (userData) => {
  if (!userData) return;

  const tracking = userData?.tracking || {};

  // GTM Event
  sendGTMEvent('userSignup', {
    userId: userData?._id,
    userEmail: userData?.email,
    signupMethod: 'email'
  }, tracking);

  // Meta Event
  sendMetaEvent('CompleteRegistration', {
    content_name: 'User Signup',
    user_id: userData?._id,
    user_email: userData?.email,
    signup_method: 'email',
    status: 'completed',
    value: 0,
    currency: 'USD'
  }, tracking);
};

/**
 * Track agent created
 * @param {Object} agentData - Agent object from API response
 */
export const trackAgentCreated = (agentData) => {
  if (!agentData) return;

  const { user, tracking } = getUserData();

  // GTM Event
  sendGTMEvent('agentCreated', {
    userId: user?._id,
    agentId: agentData?._id,
    agentName: agentData?.name
  }, tracking);

  // Meta Event
  sendMetaEvent('StartTrial', {
    content_name: 'Agent Created',
    content_category: 'Product Usage',
    user_id: user?._id,
    agent_id: agentData?._id,
    agent_name: agentData?.name,
    value: 0,
    currency: 'USD',
    predicted_ltv: 0
  }, tracking);
};

/**
 * Track contact created
 * @param {Object} contactData - Contact object from API response
 */
export const trackContactCreated = (contactData) => {
  if (!contactData) return;

  const { user, tracking } = getUserData();

  // GTM Event
  sendGTMEvent('contactCreated', {
    userId: user?._id,
    contactId: contactData?._id,
    contactName: contactData?.name
  }, tracking);

  // Meta Event
  sendMetaEvent('Contact', {
    content_name: 'Contact Created',
    content_category: 'Product Usage',
    user_id: user?._id,
    contact_id: contactData?._id,
    contact_name: contactData?.name,
    value: 0,
    currency: 'USD'
  }, tracking);
};

/**
 * Track deal created
 * @param {Object} dealData - Deal object from API response
 */
export const trackDealCreated = (dealData) => {
  if (!dealData) return;

  const { user, tracking } = getUserData();

  // GTM Event
  sendGTMEvent('dealCreated', {
    userId: user?._id,
    dealId: dealData?._id,
    dealName: dealData?.name,
    dealValue: dealData?.value || 0
  }, tracking);

  // Meta Event
  sendMetaEvent('Lead', {
    content_name: 'Deal Created',
    content_category: 'Product Usage',
    user_id: user?._id,
    deal_id: dealData?._id,
    deal_name: dealData?.name,
    deal_value: dealData?.value || 0,
    value: dealData?.value || 0,
    currency: 'USD'
  }, tracking);
};

/**
 * Track workflow created
 * @param {Object} workflowData - Workflow object from API response
 */
export const trackWorkflowCreated = (workflowData) => {
  if (!workflowData) return;

  const { user, tracking } = getUserData();

  // GTM Event
  sendGTMEvent('workflowCreated', {
    userId: user?._id,
    workflowId: workflowData?._id,
    workflowName: workflowData?.name
  }, tracking);

  // Meta Event
  sendMetaEvent('CustomizeProduct', {
    content_name: 'Workflow Created',
    content_category: 'Product Usage',
    user_id: user?._id,
    workflow_id: workflowData?._id,
    workflow_name: workflowData?.name,
    value: 0,
    currency: 'USD'
  }, tracking);
};

/**
 * Track payment info added (plan selected)
 * @param {Object} planData - Plan selection data
 * @param {string} planData.planId - Plan ID
 * @param {string} planData.priceId - Price ID
 * @param {string} planData.mode - Purchase mode (subscription/credits)
 */
export const trackPaymentInfoAdded = (planData) => {
  if (!planData) return;

  const { user, tracking } = getUserData();

  // GTM Event
  sendGTMEvent('Checkoutinilized', {
    userId: user?._id,
    planId: planData.planId,
    priceId: planData.priceId,
    purchaseType: planData.mode,
    purchaseCategory: planData.mode === 'subscription' ? 'planSubscription' : 'credits'
  }, tracking);

  // Meta Event
  sendMetaEvent('AddPaymentInfo', {
    content_name: 'Plan Selected',
    user_id: user?._id,
    plan_id: planData.planId,
    price_id: planData.priceId,
    purchase_type: planData.mode,
    purchase_category: planData.mode === 'subscription' ? 'planSubscription' : 'credits',
    value: 0,
    currency: 'USD'
  }, tracking);
};
